
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useGroups } from "@/hooks/useGroups";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { ContractGroup } from "@/lib/types";

// Define the form schema
const formSchema = z.object({
  name: z.string().min(2, {
    message: "Group name must be at least 2 characters.",
  }),
  description: z.string().optional(),
});

export type GroupFormValues = z.infer<typeof formSchema>;

interface GroupFormProps {
  mode: "create" | "edit";
  group?: ContractGroup;
  onSubmit?: (values: GroupFormValues) => void;
  isLoading?: boolean;
  defaultValues?: GroupFormValues;
}

export function GroupForm({ mode, group, onSubmit, isLoading, defaultValues }: GroupFormProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const { useCreateGroupMutation, useUpdateGroupMutation } = useGroups();
  const createGroup = useCreateGroupMutation();
  const updateGroup = useUpdateGroupMutation();

  // Initialize form
  const form = useForm<GroupFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultValues || {
      name: group?.name || "",
      description: group?.description || "",
    },
  });

  const handleSubmit = async (values: GroupFormValues) => {
    if (onSubmit) {
      onSubmit(values);
      return;
    }
    
    setIsSaving(true);

    try {
      if (mode === "edit" && group) {
        // Edit existing group
        await updateGroup.mutateAsync({
          id: group.id,
          name: values.name,
          description: values.description,
          user_id: group.user_id,
          created_at: group.created_at,
          updated_at: new Date().toISOString(),
        });
        toast({
          title: "Success",
          description: "Group updated successfully",
        });
      } else {
        // Create new group
        await createGroup.mutateAsync({
          name: values.name,
          description: values.description,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
        toast({
          title: "Success",
          description: "Group created successfully",
        });
      }
      navigate("/groups");
    } catch (error) {
      console.error("Error saving group:", error);
      toast({
        title: "Error",
        description: "Failed to save group",
        variant: "destructive",
      });
    }

    setIsSaving(false);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input placeholder="Group name" {...field} />
                </FormControl>
                <FormDescription>
                  This is the display name for your group.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Group description"
                    className="resize-none"
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  A brief description of what this group is for.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <Separator />
        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate("/groups")}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSaving || isLoading}>
            {isSaving || isLoading ? "Saving..." : mode === "create" ? "Create" : "Update"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
